var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { FormGroupContextProvider, FormTabHeader, usePermissions, useResourceContext, useRecordContext, } from 'react-admin';
import { Stack } from '@mui/material';
import { canAccess } from '../canAccess';
/**
 * Replacement for react-admin's `<TabbedForm.Tab>` that only renders a tab if the user has the right permissions.
 *
 * Add a `name` prop to the Tab to define the sub-resource that the user needs to have the right permissions for.
 *
 * `<TabbedForm.Tab>` also only renders the child inputs for which the user has the 'write' permissions.
 *
 * @example
 * import { Edit, TextInput } from 'react-admin';
 * import { TabbedForm } from '@react-admin/ra-rbac';
 *
 * const authProvider = {
 *     // ...
 *     getPermissions: () => Promise.resolve([
 *         // 'delete' is missing
 *         { action: ['list', 'edit'], resource: 'products' },
 *         { action: 'write', resource: 'products.reference' },
 *         { action: 'write', resource: 'products.width' },
 *         { action: 'write', resource: 'products.height' },
 *         // 'products.description' is missing
 *         { action: 'write', resource: 'products.thumbnail' },
 *         // 'products.image' is missing
 *         { action: 'write', resource: 'products.tab.description' },
 *         // 'products.tab.stock' is missing
 *         { action: 'write', resource: 'products.tab.images' },
 *     ]),
 * };
 *
 * const ProductEdit = () => (
 *     <Edit>
 *         <TabbedForm>
 *             <TabbedForm.Tab label="Description" name="description">
 *                 <TextInput source="reference" />
 *                 <TextInput source="width" />
 *                 <TextInput source="height" />
 *                 // not displayed
 *                 <TextInput source="description" />
 *             </TabbedForm.Tab>
 *             // not displayed
 *             <TabbedForm.Tab label="Stock" name="stock">
 *                 <TextInput source="stock" />
 *             </TabbedForm.Tab>
 *             <TabbedForm.Tab label="Images" name="images">
 *                 // not displayed
 *                 <TextInput source="image" />
 *                 <TextInput source="thumbnail" />
 *             </TabbedForm.Tab>
 *             // delete button not displayed
 *         </TabbedForm>
 *     </Edit>
 * );
 */
export var FormTab = function (props) {
    var className = props.className, contentClassName = props.contentClassName, children = props.children, hidden = props.hidden, icon = props.icon, intent = props.intent, label = props.label, name = props.name, onChange = props.onChange, path = props.path, resource = props.resource, syncWithLocation = props.syncWithLocation, value = props.value, rest = __rest(props, ["className", "contentClassName", "children", "hidden", "icon", "intent", "label", "name", "onChange", "path", "resource", "syncWithLocation", "value"]);
    var resourceFromContext = useResourceContext();
    var recordFromContext = useRecordContext();
    var _a = usePermissions(), permissions = _a.permissions, isLoading = _a.isLoading;
    var renderHeader = function () { return (React.createElement(FormTabHeader, { label: label, value: value, icon: icon, className: className, syncWithLocation: syncWithLocation, onChange: onChange })); };
    var renderContent = function () { return (React.createElement(FormGroupContextProvider, { name: value.toString() },
        React.createElement(Stack, __assign({ alignItems: "flex-start", sx: { paddingLeft: 1, paddingRight: 1 }, style: hidden ? hiddenStyle : null, className: contentClassName, id: "tabpanel-".concat(value), "aria-labelledby": "tabheader-".concat(value), "aria-hidden": hidden || undefined }, rest), React.Children.map(children, function (input) {
            return input &&
                canAccess({
                    permissions: permissions,
                    action: 'write',
                    resource: "".concat(resource, ".").concat(input.props.source),
                    record: recordFromContext,
                })
                ? input
                : null;
        })))); };
    if (isLoading ||
        !canAccess({
            permissions: permissions,
            action: 'write',
            resource: "".concat(resourceFromContext, ".tab.").concat(name),
            record: recordFromContext,
        })) {
        return null;
    }
    return intent === 'header' ? renderHeader() : renderContent();
};
var hiddenStyle = { display: 'none' };
FormTab.displayName = 'FormTab';
